import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Box,
  Alert,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Avatar,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  ContentCopy as ContentCopyIcon,
  WhatsApp as WhatsAppIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import * as Sentry from "@sentry/react";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";

const API_BASE = import.meta.env.VITE_API_BASE;

const DropZone = styled("div")(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  textAlign: "center",
  cursor: "pointer",
  backgroundColor: theme.palette.action.hover,
  marginBottom: theme.spacing(2),
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
}));

// ✅ UPDATED SCHEMA: Added .trim() to ensure spaces aren't accepted
const validationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .required("Contact Person Name is required"),
  username: Yup.string()
    .trim(), 
  email: Yup.string()
    .trim()
    .email("Enter a valid email")
    .required("Email is required"),
  mobile_number: Yup.string()
    .trim(),
  company_name: Yup.string()
    .trim()
    .required("Company Name is required"),
  role: Yup.string()
    .trim()
    .required("Role / Title is required"), // This prevents " " (space) submission
  company_url: Yup.string()
    .trim()
    .url("Enter a valid URL (include http/https)"),
  description: Yup.string()
    .trim(),
});

export const UserCreationPage = () => {
  const [images, setImages] = useState([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState("");
  const [createdUser, setCreatedUser] = useState(null);

  const formik = useFormik({
    initialValues: {
      name: "",
      username: "",
      email: "",
      mobile_number: "",
      company_name: "",
      role: "",
      company_url: "",
      description: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setError("");
      setCreatedUser(null);
      
      // ✅ Explicitly trimming values before sending to API
      const cleanValues = {
        ...values,
        name: values.name.trim(),
        username: values.username.trim(),
        email: values.email.trim(),
        mobile_number: values.mobile_number.trim(),
        company_name: values.company_name.trim(),
        role: values.role.trim(),
        company_url: values.company_url.trim(),
        description: values.description.trim(),
      };

      try {
        const response = await axios.post(`${API_BASE}/create-user`, cleanValues, {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        });

        const result = response.data;

        if (result.success) {
          setCreatedUser(result.newUser);
          resetForm();
          setImages([]);
        } else {
          setError(result.message || "Failed to create the user.");
        }
      } catch (err) {
        if (err.response && err.response.data && err.response.data.message) {
          setError(err.response.data.message);
        } else {
          setError("A network error occurred while saving the user.");
        }
        
        if (err.response && err.response.status >= 500) {
          Sentry.captureException(err, {
            tags: { section: "user-create", source: "create-user" },
            extra: {
              status: err.response.status,
              response: err.response.data,
              endpoint: `${API_BASE}/create-user`,
            },
          });
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleFileChange = (files) => {
    const validFiles = Array.from(files).slice(0, 3);
    setImages(validFiles);
    setError("");
    setCreatedUser(null);
    if (validFiles.length > 0) handleImageExtract(validFiles);
  };

const usernameCreateFromEmail = (email) => {
    if (email && typeof email === "string") {
      return email.split("@")[0] || "";
    }
    return "";
  };

  const handleImageExtract = async (files) => {
    setIsExtracting(true);
    const uploadData = new FormData();
    files.forEach((file, index) => uploadData.append(`image${index}`, file));

    try {
      const response = await fetch(`${API_BASE}/extract-info-from-images`, {
        method: "POST",
        credentials: "include",
        body: uploadData,
      });
      const result = await response.json();

      if (result.success && result.data) {
        // Merge extracted data into Formik state
        formik.setValues((prevValues) => ({
          ...prevValues,
          ...result.data,
          company_url: result.data.website || result.data.company_url || prevValues.company_url,
          username: usernameCreateFromEmail(result?.data?.email) || prevValues.username,
        }));
      } else {
        setError(result.message || "Failed to extract data from images.");
      }
    } catch (err) {
      setError("An error occurred during image processing.");
      Sentry.captureException(err);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleCopyToClipboard = (text) => navigator.clipboard.writeText(text);
  const removeImage = (indexToRemove) =>
    setImages(images.filter((_, index) => index !== indexToRemove));

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h4" gutterBottom>
          Create New User
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Upload up to 3 images. Our AI will attempt to pre-fill the form.
        </Typography>
        
        <label htmlFor="file-upload">
          <input
            id="file-upload"
            type="file"
            multiple
            hidden
            accept="image/*"
            onChange={(e) => handleFileChange(e.target.files)}
          />
          <DropZone>
            <CloudUploadIcon sx={{ fontSize: 48, color: "primary.main" }} />
            <Typography>Drag & drop images here or click to select</Typography>
          </DropZone>
        </label>

        {isExtracting && (
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <CircularProgress size={24} sx={{ mr: 1 }} />
            <Typography>Analyzing images...</Typography>
          </Box>
        )}

        {images.length > 0 && (
          <List dense>
            {images.map((file, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <IconButton edge="end" onClick={() => removeImage(index)}>
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <Avatar sx={{ mr: 2 }}>
                  <ImageIcon />
                </Avatar>
                <ListItemText
                  primary={file.name}
                  secondary={`${(file.size / 1024).toFixed(1)} KB`}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          User & Company Details
        </Typography>

        {/* Start Form */}
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            <Grid item size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Contact Person Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                required
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                id="username"
                name="username"
                label="Username (optional)"
                value={formik.values.username}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText="Leave blank to auto-generate"
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                required
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                id="mobile_number"
                name="mobile_number"
                label="Mobile Number"
                value={formik.values.mobile_number}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.mobile_number && Boolean(formik.errors.mobile_number)}
                helperText={formik.touched.mobile_number && formik.errors.mobile_number}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                id="company_name"
                name="company_name"
                label="Company Name"
                value={formik.values.company_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.company_name && Boolean(formik.errors.company_name)}
                helperText={formik.touched.company_name && formik.errors.company_name}
                required
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                id="role"
                name="role"
                label="Role / Title"
                value={formik.values.role}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.role && Boolean(formik.errors.role)}
                helperText={formik.touched.role && formik.errors.role}
                required
              />
            </Grid>
            <Grid item size={{ xs: 12 }}>
              <TextField
                fullWidth
                id="company_url"
                name="company_url"
                label="Company Website URL"
                value={formik.values.company_url}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.company_url && Boolean(formik.errors.company_url)}
                helperText={formik.touched.company_url && formik.errors.company_url}
              />
            </Grid>
            <Grid item size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                id="description"
                name="description"
                label="Company Description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Grid>
          </Grid>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={formik.isSubmitting || isExtracting}
            sx={{ mt: 3, py: 1.5, px: 4 }}
          >
            {formik.isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Save User"
            )}
          </Button>
        </form>

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        {/* Success / Created User Card */}
        {createdUser && (
          <Card sx={{ mt: 4, bgcolor: "grey.100" }}>
            <CardContent>
              <Box
                display={"flex"}
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Typography variant="h6" color="success.dark">
                  User Created Successfully!
                </Typography>

                <IconButton
                  onClick={() => {
                    const welcomeText = `Welcome! Login details for Card Vision:\nURL: ${createdUser.app_url}\nUsername: ${createdUser.username}\nEmail: ${createdUser.email}\nPassword: ${createdUser.password}
                    
\nIt was great meeting you today at the exhibition. We are an Artificial intelligence-first company, and the tool above allows you to automatically scan all leads you had today , create custom message for each and send them so you don't miss any leads and you reach out to leads the same day you had a conversation.
\n
To get access, please reply back here. It's very affordable for launch price 1399 inr for entire exhibition, unlimited contacts. 
\n
Get more out of every exhibition today
                    `;
                    handleCopyToClipboard(welcomeText);
                  }}
                >
                  <ContentCopyIcon />
                </IconButton>
              </Box>
              {Object.entries(createdUser)
                .filter(([key]) => key !== "mobile_number")
                .map(([key, value]) => (
                  <TextField
                    key={key}
                    label={key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                    value={value}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                  />
                ))}
              {createdUser.mobile_number && (
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<WhatsAppIcon />}
                  onClick={() => {
                    const welcomeText = encodeURIComponent(
                      `Welcome! Login details for Card Vision:\nURL: ${createdUser.app_url}\nUsername: ${createdUser.username}\nEmail: ${createdUser.email}\nPassword: ${createdUser.password}
\nIt was great meeting you today at the exhibition. We are an Artificial intelligence-first company, and the tool above allows you to automatically scan all leads you had today , create custom message for each and send them so you don't miss any leads and you reach out to leads the same day you had a conversation.
\n
To get access, please reply back here. It's very affordable for launch price 1399 inr for entire exhibition, unlimited contacts. 
\n
Get more out of every exhibition today`
                    );
                    window.open(
                      `https://wa.me/91${createdUser.mobile_number}?text=${welcomeText}`,
                      "_blank"
                    );
                  }}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    bgcolor: "#25D366",
                    "&:hover": { bgcolor: "#128C7E" },
                  }}
                >
                  Send Credentials via WhatsApp
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </Paper>
    </Container>
  );
};